import type { AxiosInstance } from 'axios';
import type { paths } from '../schema';
import type { TimelineEntry } from '../types/timeline';
import type { RequestOptions, RequestOptionsWithParams } from './types';
import { curry1 } from './utils';

type PermissionGroupTimelineEntry = TimelineEntry<
  Record<string, any>,
  | 'permission_group_created'
  | 'permission_group_name_updated'
  | 'permission_group_permissions_updated'
  | 'permission_group_deleted'
>;

export type GetPermissionGroupsParams =
  paths['/api/permission-group']['get']['parameters']['query'] & {
    include_summary?: boolean;
  };

// PermissionGroup & PermissionGroupRequest are almost entirely incomplete
type PermissionGroupRequest = Record<string, any> &
  paths['/api/permission-group']['post']['requestBody']['content']['application/json'];
export type PermissionGroup = Record<string, any> &
  paths['/api/permission-group']['post']['responses']['201']['content']['application/json'];

export type PermissionGroupHistory = Omit<
  paths['/api/permission-group/{permission_group_id}/history']['get']['responses']['200']['content']['application/json'],
  'results'
> & {
  results: PermissionGroupTimelineEntry[];
};

export type PermissionGroupMetadata =
  paths['/api/permissions/meta-data']['get']['responses']['200']['content']['application/json'];

export type PermissionGroupHistoryParams = Required<
  paths['/api/permission-group/{permission_group_id}/history']['get']['parameters']
>['query'];

export const createPermissionGroup = async (
  instance: AxiosInstance,
  data: PermissionGroupRequest,
  options?: RequestOptions
): Promise<PermissionGroup> => {
  const res = await instance.post<PermissionGroup>(
    '/permission-group',
    data,
    options
  );

  return res.data;
};

export const getPermissionGroup = async (
  instance: AxiosInstance,
  id: string,
  options?: RequestOptions
): Promise<PermissionGroup> => {
  const res = await instance.get<PermissionGroup>(
    `/permission-group/${id}`,
    options
  );

  return res.data;
};

export const getPermissionGroups = async (
  instance: AxiosInstance,
  options?: RequestOptionsWithParams<GetPermissionGroupsParams>
): Promise<PermissionGroup[]> => {
  const res = await instance.get<PermissionGroup[]>(
    '/permission-group',
    options
  );

  return res.data;
};

export const getPermissionGroupHistory = async (
  instance: AxiosInstance,
  id: string,
  options?: RequestOptionsWithParams<PermissionGroupHistoryParams>
): Promise<PermissionGroupHistory> => {
  const res = await instance.get<PermissionGroupHistory>(
    `/permission-group/${id}/history`,
    options
  );

  return res.data;
};

export const getPermissionGroupMetadata = async (
  instance: AxiosInstance,
  options?: RequestOptions
): Promise<PermissionGroupMetadata> => {
  const res = await instance.get<PermissionGroupMetadata>(
    '/permissions/meta-data',
    options
  );

  return res.data;
};

export class PermissionGroupService {
  public createPermissionGroup;
  public getPermissionGroup;
  public getPermissionGroups;
  public getPermissionGroupHistory;
  public getPermissionGroupMetadata;

  constructor(private instance: AxiosInstance) {
    this.createPermissionGroup = curry1(createPermissionGroup)(this.instance);
    this.getPermissionGroup = curry1(getPermissionGroup)(this.instance);
    this.getPermissionGroups = curry1(getPermissionGroups)(this.instance);
    this.getPermissionGroupHistory = curry1(getPermissionGroupHistory)(
      this.instance
    );
    this.getPermissionGroupMetadata = curry1(getPermissionGroupMetadata)(
      this.instance
    );
  }
}
